import { ForestGenerator } from './ForestGenerator.js';
import { VillageGenerator } from './VillageGenerator.js';
import { DungeonGenerator } from './DungeonGenerator.js';
import { CaveGenerator } from './CaveGenerator.js';
import { RuinGenerator } from './RuinGenerator.js';
import { InteriorGenerator } from './InteriorGenerator.js';
import { SuburbanHomeGenerator } from './SuburbanHomeGenerator.js';
import { GasStationGenerator } from './GasStationGenerator.js';
import { ApartmentComplexGenerator } from './ApartmentComplexGenerator.js';
import { MiniMallGenerator } from './MiniMallGenerator.js';

export const SIZE_PRESETS = {
  tiny:   { width: 20, height: 15, label: 'Tiny (20x15)' },
  small:  { width: 40, height: 30, label: 'Small (40x30)' },
  medium: { width: 64, height: 48, label: 'Medium (64x48)' },
  large:  { width: 100, height: 75, label: 'Large (100x75)' },
  huge:   { width: 150, height: 100, label: 'Huge (150x100)' },
};

export const GENERATOR_TYPES = {
  forest: {
    name: 'Forest',
    description: 'Outdoor map with trees, paths, clearings, and water features',
    class: ForestGenerator,
    defaultOptions: ForestGenerator.DEFAULT_OPTIONS,
    defaultTiles: ForestGenerator.DEFAULT_TILES,
  },
  village: {
    name: 'Village',
    description: 'Settlement with buildings, roads, gardens, and a town square',
    class: VillageGenerator,
    defaultOptions: VillageGenerator.DEFAULT_OPTIONS,
    defaultTiles: VillageGenerator.DEFAULT_TILES,
  },
  dungeon: {
    name: 'Dungeon',
    description: 'BSP dungeon with rooms, corridors, and doors',
    class: DungeonGenerator,
    defaultOptions: DungeonGenerator.DEFAULT_OPTIONS,
    defaultTiles: DungeonGenerator.DEFAULT_TILES,
  },
  cave: {
    name: 'Cave',
    description: 'Organic cave system via cellular automata with water and crystals',
    class: CaveGenerator,
    defaultOptions: CaveGenerator.DEFAULT_OPTIONS,
    defaultTiles: CaveGenerator.DEFAULT_TILES,
  },
  ruin: {
    name: 'Ruins',
    description: 'Crumbling structures with overgrowth and debris',
    class: RuinGenerator,
    defaultOptions: RuinGenerator.DEFAULT_OPTIONS,
    defaultTiles: RuinGenerator.DEFAULT_TILES,
  },
  interior: {
    name: 'Interior',
    description: 'Building interior with rooms, doors, and furniture',
    class: InteriorGenerator,
    defaultOptions: InteriorGenerator.DEFAULT_OPTIONS,
    defaultTiles: InteriorGenerator.DEFAULT_TILES,
  },
  suburban_home: {
    name: 'Suburban Home',
    description: 'Residential house with BSP rooms, yard, road, driveway, and fenced backyard',
    class: SuburbanHomeGenerator,
    defaultOptions: SuburbanHomeGenerator.DEFAULT_OPTIONS,
    defaultTiles: SuburbanHomeGenerator.DEFAULT_TILES,
  },
  gas_station: {
    name: 'Gas Station',
    description: 'Convenience store with pump islands, parking lot, and road',
    class: GasStationGenerator,
    defaultOptions: GasStationGenerator.DEFAULT_OPTIONS,
    defaultTiles: GasStationGenerator.DEFAULT_TILES,
  },
  apartment: {
    name: 'Apartment Complex',
    description: 'Multiple buildings with units around a central parking lot',
    class: ApartmentComplexGenerator,
    defaultOptions: ApartmentComplexGenerator.DEFAULT_OPTIONS,
    defaultTiles: ApartmentComplexGenerator.DEFAULT_TILES,
  },
  mini_mall: {
    name: 'Mini-Mall',
    description: 'Strip mall with typed stores, back storage, and shared parking',
    class: MiniMallGenerator,
    defaultOptions: MiniMallGenerator.DEFAULT_OPTIONS,
    defaultTiles: MiniMallGenerator.DEFAULT_TILES,
  },
};

/**
 * Create and run a generator.
 * @param {string} type - Generator type key
 * @param {object} options - { width, height, seed, ...generatorOptions }
 * @param {object} content - Content provider data (tiles, tilesets, etc.)
 * @returns {object} TMJ map data
 */
export function runGenerator(type, options, content) {
  const entry = GENERATOR_TYPES[type];
  if (!entry) throw new Error(`Unknown generator type: ${type}`);

  const generator = new entry.class(options);
  return generator.generate(content);
}
